import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import { continueInRovoChat } from '@atlaskit/editor-common/keymaps';
import { type EditorAgent } from '@atlassian/generative-ai-modal/utils/agents';

import { type EditorPluginAIProvider } from '../../../../types/types';
import { ChatIcon } from '../../../icons/prompt/chat';
import type {
	ActionAppearance,
	EditorPluginAIConfigItemAgentAction,
	EditorPluginAIConfigItemFactoryOptions,
	EditorPluginAIConfigItemGenericChatAction,
} from '../config-items';

type CreateContinueInChatProps = {
	actionOverride?: ({ channelId }: { channelId: string }) => void;
	actionSideEffect?: () => void;
	appearance: ActionAppearance;
	factoryOptions?: EditorPluginAIConfigItemFactoryOptions;
	product: EditorPluginAIProvider['product'];
};

export const createContinueInChat = ({
	appearance,
	product,
	actionSideEffect,
	factoryOptions,
}: CreateContinueInChatProps): EditorPluginAIConfigItemGenericChatAction => ({
	type: 'generic-chat',
	title: factoryOptions?.isInDocumentStreamingEnabled
		? aiConfigItemMessages.configActionContinueChatLabel
		: aiConfigItemMessages.configActionContinueInChatLabel,
	appearance,
	shortcut: continueInRovoChat,
	run: ({ name, dialogues, publish }) => {
		if (!publish || !dialogues.length) {
			return;
		}

		publish({ type: 'chat-new', product, data: { dialogues, name }, source: 'editor' });

		actionSideEffect?.();
	},
	preserveEditorSelectionOnComplete: true,
	icon: ChatIcon,
});

type CreateContinueInChatForAgentProps = CreateContinueInChatProps & { agent: EditorAgent };

export const createContinueInChatForAgent = ({
	appearance,
	agent,
	product,
	actionSideEffect,
	actionOverride,
	factoryOptions,
}: CreateContinueInChatForAgentProps): EditorPluginAIConfigItemAgentAction => ({
	type: 'agent',
	title: factoryOptions?.isInDocumentStreamingEnabled
		? aiConfigItemMessages.configActionContinueChatLabel
		: aiConfigItemMessages.configActionContinueInChatLabel,
	shortcut: continueInRovoChat,
	appearance,
	run: ({ channelId, publish }) => {
		if (!channelId || !publish) {
			return;
		}

		if (typeof actionOverride === 'function') {
			actionOverride({ channelId });
			return;
		}

		publish({
			type: 'chat-open',
			product,
			data: { channelId, agentId: agent.namedId },
			source: 'editor',
		});

		actionSideEffect?.();
	},
	preserveEditorSelectionOnComplete: true,
	icon: ChatIcon,
});
